/**
 * LensComparisonPanel Component
 *
 * Side-by-side comparison of two model responses for a specific criterion.
 * Shows Agree/Mixed/Disagree chips, consensus meters, and highlighted text differences.
 *
 * Part of Consensus Heatmap Comparison feature (behind newDesign flag)
 * Enhanced with sentence-level highlighting (Task 2)
 */

import React, { useState } from 'react';
import { getModelDisplayInfo } from '../../../utils/modelDisplayName';
import { getModelLogo } from '../../../utils/logoMapping';
import type { Criterion, PerModelState } from '../types';
import { CRITERION_METADATA } from '../types';
import { useSentenceAnalysis, type SentenceLabel } from '../../../hooks/useSentenceAnalysis';

export interface LensComparisonPanelProps {
  criterion: Criterion;
  modelAKey: string;
  modelBKey: string;
  responseA: string;
  responseB: string;
  stateA: PerModelState;
  stateB: PerModelState;
  scoreA: number; // 0-100
  scoreB: number; // 0-100
  judgeAnalysis?: string;
  onClose: () => void;
  onExpandResponse?: (side: 'left' | 'right') => void;
  comparisonId?: string; // Task 2: Added for sentence analysis API
}

const STATE_COLORS: Record<PerModelState, { bg: string; text: string; symbol: string; border: string }> = {
  agree: { bg: 'bg-green-500/20', text: 'text-green-400', symbol: '✓', border: 'border-green-500' },
  mixed: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', symbol: '≈', border: 'border-yellow-500' },
  differ: { bg: 'bg-red-500/20', text: 'text-red-400', symbol: '✕', border: 'border-red-500' }
};

// Sentence label colors for highlighting
const SENTENCE_LABEL_COLORS: Record<'shared' | 'unique' | 'conflict', string> = {
  shared: 'bg-green-500/10 border-l-2 border-green-500/50',
  unique: 'bg-blue-500/10 border-l-2 border-blue-500/50',
  conflict: 'bg-red-500/10 border-l-2 border-red-500/50'
};

export const LensComparisonPanel: React.FC<LensComparisonPanelProps> = ({
  criterion,
  modelAKey,
  modelBKey,
  responseA,
  responseB,
  stateA,
  stateB,
  scoreA,
  scoreB,
  judgeAnalysis,
  onClose,
  onExpandResponse,
  comparisonId
}) => {
  const [expandedA, setExpandedA] = useState(false);
  const [expandedB, setExpandedB] = useState(false);
  const [highlightMode, setHighlightMode] = useState(false);

  const criterionMeta = CRITERION_METADATA[criterion];
  const displayInfoA = getModelDisplayInfo(modelAKey.split(':')[1]);
  const displayInfoB = getModelDisplayInfo(modelBKey.split(':')[1]);
  const logoA = getModelLogo(modelAKey.split(':')[1]);
  const logoB = getModelLogo(modelBKey.split(':')[1]);

  const stateColorsA = STATE_COLORS[stateA];
  const stateColorsB = STATE_COLORS[stateB];

  // Fetch sentence analysis when highlight mode is ON
  const { data: sentenceData, loading: sentenceLoading, error: sentenceError } = useSentenceAnalysis(
    comparisonId,
    criterion,
    modelAKey,
    modelBKey,
    highlightMode
  );

  // Truncate response for initial view
  const truncateResponse = (text: string, expanded: boolean) => {
    if (expanded) return text;
    const lines = text.split('\n');
    if (lines.length <= 8) return text;
    return lines.slice(0, 8).join('\n') + '\n...';
  };

  // Render response with sentence highlighting
  const renderHighlightedResponse = (
    text: string,
    sentences: SentenceLabel[] | undefined,
    expanded: boolean
  ) => {
    if (!highlightMode || !sentences || sentences.length === 0) {
      return (
        <pre className="whitespace-pre-wrap text-slate-200 text-sm font-sans">
          {truncateResponse(text, expanded)}
        </pre>
      );
    }

    return (
      <div className="space-y-2">
        {sentences.map((sentence, idx) => (
          <div
            key={sentence.id}
            className={`px-3 py-2 rounded ${SENTENCE_LABEL_COLORS[sentence.label]}`}
          >
            <span className="text-slate-200 text-sm">{sentence.text}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      className="mt-6 bg-gradient-to-r from-cyan-900/30 to-blue-900/30 rounded-xl border border-cyan-700/50 p-6"
      data-testid="lens-panel"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{criterionMeta.icon}</span>
          <div>
            <h3 className="text-lg font-semibold text-white">
              Lens: {criterionMeta.label} Comparison
            </h3>
            <p className="text-sm text-cyan-200">{criterionMeta.description}</p>
          </div>
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto">
          {/* Sentence Highlight Toggle (Task 2) */}
          {comparisonId && (
            <button
              onClick={() => setHighlightMode(!highlightMode)}
              className={`px-4 py-2 min-h-[44px] rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                highlightMode
                  ? 'bg-cyan-600 hover:bg-cyan-700 text-white'
                  : 'bg-slate-700 hover:bg-slate-600 text-white'
              }`}
              data-testid="highlight-toggle"
              title="Highlight shared/unique/conflict sentences"
            >
              <span className="text-lg">{highlightMode ? '🔍' : '📝'}</span>
              <span className="hidden sm:inline">
                {highlightMode ? 'Highlighting ON' : 'Highlight Sentences'}
              </span>
              <span className="sm:hidden">
                {highlightMode ? 'ON' : 'OFF'}
              </span>
            </button>
          )}

          <button
            onClick={onClose}
            className="px-4 py-2 min-h-[44px] rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-sm font-medium transition-colors"
            data-testid="lens-close-button"
          >
            ✕ Close
          </button>
        </div>
      </div>

      {/* Sentence Analysis Loading/Error State */}
      {highlightMode && sentenceLoading && (
        <div className="mb-4 p-3 bg-slate-800/30 rounded-lg border border-slate-700">
          <div className="flex items-center gap-2 text-sm text-cyan-400">
            <div className="w-4 h-4 border-2 border-cyan-400/20 border-t-cyan-400 rounded-full animate-spin"></div>
            <span>Analyzing sentences...</span>
          </div>
        </div>
      )}

      {highlightMode && sentenceError && (
        <div className="mb-4 p-3 bg-red-500/10 rounded-lg border border-red-500/30">
          <p className="text-sm text-red-400">
            ⚠️ Sentence analysis unavailable. Showing plain text.
          </p>
        </div>
      )}

      {/* Sentence Legend (when highlighting is ON) */}
      {highlightMode && sentenceData && !sentenceLoading && (
        <div className="mb-4 p-3 bg-slate-800/30 rounded-lg border border-slate-700">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span className="text-slate-400 font-medium">Legend:</span>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 bg-green-500/20 border-l-2 border-green-500/50"></div>
              <span className="text-slate-300">Shared</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 bg-blue-500/20 border-l-2 border-blue-500/50"></div>
              <span className="text-slate-300">Unique</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 bg-red-500/20 border-l-2 border-red-500/50"></div>
              <span className="text-slate-300">Conflict</span>
            </div>
            <span className="text-slate-500 text-xs ml-auto">
              {sentenceData.analysisMethod === 'heuristic' ? 'Heuristic analysis' : 'AI analysis'}
              {' '}({sentenceData.confidence}% confidence)
            </span>
          </div>
        </div>
      )}

      {/* Side-by-side comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Model A */}
        <div className="bg-slate-800/50 rounded-lg border border-slate-700" data-testid="lens-model-a">
          {/* Model Header */}
          <div className="p-4 border-b border-slate-700 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {logoA && <img src={logoA} alt="" className="w-6 h-6 object-contain" />}
              <div>
                <div className="font-medium text-white">{displayInfoA.displayName}</div>
                <div className="text-xs text-slate-400">{displayInfoA.providerName}</div>
              </div>
            </div>
            {onExpandResponse && (
              <button
                onClick={() => onExpandResponse('left')}
                className="text-cyan-400 hover:text-cyan-300 text-2xl transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                title="Open full response"
                data-testid="expand-button-left"
              >
                ⤢
              </button>
            )}
          </div>

          {/* State Chip */}
          <div className="p-4 border-b border-slate-700">
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg ${stateColorsA.bg} ${stateColorsA.text} border ${stateColorsA.border}`}>
              <span className="text-lg">{stateColorsA.symbol}</span>
              <span className="font-medium capitalize">{stateA}</span>
              <span className="text-sm opacity-75">{scoreA}%</span>
            </div>
          </div>

          {/* Consensus Meter */}
          <div className="p-4 border-b border-slate-700">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-slate-400 uppercase tracking-wide">Consensus</span>
              <span className="text-sm font-medium text-white">{scoreA}%</span>
            </div>
            <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  scoreA >= 80 ? 'bg-gradient-to-r from-green-500 to-green-600' :
                  scoreA >= 50 ? 'bg-gradient-to-r from-yellow-500 to-yellow-600' :
                  'bg-gradient-to-r from-red-500 to-red-600'
                }`}
                style={{ width: `${scoreA}%` }}
              />
            </div>
          </div>

          {/* Response Text */}
          <div className="p-4">
            <div className="text-xs text-slate-400 uppercase tracking-wide mb-2">Response:</div>
            <div className="prose prose-sm prose-invert max-w-none">
              {renderHighlightedResponse(responseA, sentenceData?.sentencesA, expandedA)}
            </div>
            {!expandedA && !highlightMode && responseA.split('\n').length > 8 && (
              <button
                onClick={() => setExpandedA(true)}
                className="mt-3 text-sm text-cyan-400 hover:text-cyan-300 font-medium min-h-[44px] px-3"
              >
                Show Full Response ↓
              </button>
            )}
          </div>
        </div>

        {/* Model B */}
        <div className="bg-slate-800/50 rounded-lg border border-slate-700" data-testid="lens-model-b">
          {/* Model Header */}
          <div className="p-4 border-b border-slate-700 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {logoB && <img src={logoB} alt="" className="w-6 h-6 object-contain" />}
              <div>
                <div className="font-medium text-white">{displayInfoB.displayName}</div>
                <div className="text-xs text-slate-400">{displayInfoB.providerName}</div>
              </div>
            </div>
            {onExpandResponse && (
              <button
                onClick={() => onExpandResponse('right')}
                className="text-cyan-400 hover:text-cyan-300 text-2xl transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                title="Open full response"
                data-testid="expand-button-right"
              >
                ⤢
              </button>
            )}
          </div>

          {/* State Chip */}
          <div className="p-4 border-b border-slate-700">
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg ${stateColorsB.bg} ${stateColorsB.text} border ${stateColorsB.border}`}>
              <span className="text-lg">{stateColorsB.symbol}</span>
              <span className="font-medium capitalize">{stateB}</span>
              <span className="text-sm opacity-75">{scoreB}%</span>
            </div>
          </div>

          {/* Consensus Meter */}
          <div className="p-4 border-b border-slate-700">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-slate-400 uppercase tracking-wide">Consensus</span>
              <span className="text-sm font-medium text-white">{scoreB}%</span>
            </div>
            <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  scoreB >= 80 ? 'bg-gradient-to-r from-green-500 to-green-600' :
                  scoreB >= 50 ? 'bg-gradient-to-r from-yellow-500 to-yellow-600' :
                  'bg-gradient-to-r from-red-500 to-red-600'
                }`}
                style={{ width: `${scoreB}%` }}
              />
            </div>
          </div>

          {/* Response Text */}
          <div className="p-4">
            <div className="text-xs text-slate-400 uppercase tracking-wide mb-2">Response:</div>
            <div className="prose prose-sm prose-invert max-w-none">
              {renderHighlightedResponse(responseB, sentenceData?.sentencesB, expandedB)}
            </div>
            {!expandedB && !highlightMode && responseB.split('\n').length > 8 && (
              <button
                onClick={() => setExpandedB(true)}
                className="mt-3 text-sm text-cyan-400 hover:text-cyan-300 font-medium min-h-[44px] px-3"
              >
                Show Full Response ↓
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Judge Analysis (if provided) */}
      {judgeAnalysis && (
        <div className="mt-6 p-4 bg-slate-800/30 rounded-lg border border-slate-700">
          <div className="flex items-start gap-3">
            <span className="text-2xl">👨‍⚖️</span>
            <div className="flex-1">
              <div className="text-sm font-medium text-white mb-2">Judge Analysis:</div>
              <div className="text-sm text-slate-300">{judgeAnalysis}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
